import { Button, ButtonColors } from '@chiroup/components/Button';
import React, { useState } from 'react';
import {
  NEUROLOGICAL_ABBREVIATIONS,
  NEUROLOGICAL_CENTRAL_DEFAULTS,
  NEUROLOGICAL_CERVICAL_ROWS,
  NEUROLOGICAL_CRANIAL_DEFAULTS,
  NEUROLOGICAL_LOWER_ROWS,
  NEUROLOGICAL_LUMBAR_ROWS,
  NEUROLOGICAL_NOT_ASSESSED,
  NEUROLOGICAL_NO_DROPDOWN,
  NEUROLOGICAL_PERIPHERAL_OPTIONS,
  NEUROLOGICAL_PERIPHERAL_TD_OPTIONS,
  NEUROLOGICAL_SPINAL_OPTIONS,
  NEUROLOGICAL_TD_OPTIONS,
  NEUROLOGICAL_UPPER_ROWS,
  NEUROLOGICAL_WIDE_COLS,
} from '../../../../../constants';
import CentralEditableChart from './CentralEditableChart';
import CranialEditableChart from './CranialEditableChart';
import EditableChart from './EditableChart';
import NeurologicalRows from './NeurologicalRows';

const normalVals = (rowArr: string[], colArr: { text: string; normalDefaultValue: string }[]) => {
  return rowArr.reduce((obj: any, row: string) => {
    colArr.forEach(col => {
      if (
        !NEUROLOGICAL_NO_DROPDOWN?.includes(`${row}${col.text}`) &&
        !NEUROLOGICAL_NOT_ASSESSED.includes(`${row}${col.text}`)
      ) {
        obj[row] = {
          ...obj[row],
          [col.text]: {
            left: { choice: col.normalDefaultValue },
            right: { choice: col.normalDefaultValue },
          },
        };
      }
    });

    return obj;
  }, {});
};

const detailInfo: { [key in string]: any } = {
  cervical: normalVals(NEUROLOGICAL_CERVICAL_ROWS, NEUROLOGICAL_SPINAL_OPTIONS),
  lumbar: normalVals(NEUROLOGICAL_LUMBAR_ROWS, NEUROLOGICAL_SPINAL_OPTIONS),
  upper: normalVals(NEUROLOGICAL_UPPER_ROWS, NEUROLOGICAL_PERIPHERAL_OPTIONS),
  lower: normalVals(NEUROLOGICAL_LOWER_ROWS, NEUROLOGICAL_PERIPHERAL_OPTIONS),
  cranial: NEUROLOGICAL_CRANIAL_DEFAULTS,
  central: NEUROLOGICAL_CENTRAL_DEFAULTS,
};

type Props = {
  close?: () => void;
  value: {
    [key: string]: any;
  };
  onChange: (detail: any) => void;
  signed?: boolean;
};

const UnifiedNeurological: React.FC<Props> = ({ close, value, onChange, signed }) => {
  const [editableCervicalChartIsOpen, setEditableCervicalChartIsOpen] = useState(false);
  const [editableLumbarChartIsOpen, setEditableLumbarChartIsOpen] = useState(false);
  const [editableUpperChartIsOpen, setEditableUpperChartIsOpen] = useState(false);
  const [editableLowerChartIsOpen, setEditableLowerChartIsOpen] = useState(false);
  const [editableCranialChartIsOpen, setEditableCranialChartIsOpen] = useState(false);
  const [editableCentralChartIsOpen, setEditableCentralChartIsOpen] = useState(false);

  const onChangeAllNormal = () => {
    if (signed) return;

    const newDetail = {
      cervical: {
        result: 'normal',
        detail: detailInfo.cervical,
      },
      lumbar: {
        result: 'normal',
        detail: detailInfo.lumbar,
      },
      upper: {
        result: 'normal',
        detail: detailInfo.upper,
      },
      lower: {
        result: 'normal',
        detail: detailInfo.lower,
      },
      cranial: { result: 'normal', detail: detailInfo.cranial },
      central: { result: 'normal', detail: detailInfo.central },
    };
    onChange(newDetail);
  };

  const onChangeValue = (rowName: string, val: string) => {
    if (signed) return;

    const newDetail = { ...value };

    if (val === 'normal') {
      newDetail[rowName] = {
        result: 'normal',
        detail: detailInfo[rowName],
      };
    } else if (val === 'abnormal') {
      newDetail[rowName] = !newDetail?.[rowName]?.detail
        ? {
            result: 'abnormal',
            detail: detailInfo[rowName],
          }
        : { ...newDetail[rowName], result: 'abnormal' };
    } else if (val === 'not assessed') {
      delete newDetail[rowName];
    }

    onChange(newDetail);
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-row mb-4 p-2">
        <Button
          onClick={onChangeAllNormal}
          text="All normal"
          color={ButtonColors.plainWithBorder}
          disabled={signed}
        />
      </div>
      <div className="p-4 hover:bg-gray-50 dark:hover:bg-dark-gray-900">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-dark-gray-50 mb-4">
          Spinal
        </h3>
        <p className="text-sm text-gray-500 dark:text-dark-gray-300 mb-4">
          Select a spinal region below to view detail.
        </p>
        <NeurologicalRows
          subheading="Cervical"
          setChartOpen={setEditableCervicalChartIsOpen}
          onChange={onChangeValue}
          rowName="cervical"
          value={value}
          disabled={signed}
        />
        <NeurologicalRows
          subheading="Lumbar"
          setChartOpen={setEditableLumbarChartIsOpen}
          onChange={onChangeValue}
          rowName="lumbar"
          value={value}
          disabled={signed}
        />
      </div>
      <div className="p-4 hover:bg-gray-50 dark:hover:bg-dark-gray-900">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-dark-gray-50 mb-4">
          Peripheral
        </h3>
        <p className="text-sm text-gray-500 dark:text-dark-gray-300 mb-4">
          Select a peripheral region below to view detail.
        </p>
        <NeurologicalRows
          subheading="Upper"
          setChartOpen={setEditableUpperChartIsOpen}
          onChange={onChangeValue}
          rowName="upper"
          value={value}
          disabled={signed}
        />
        <NeurologicalRows
          subheading="Lower"
          setChartOpen={setEditableLowerChartIsOpen}
          onChange={onChangeValue}
          rowName="lower"
          value={value}
          disabled={signed}
        />
      </div>
      <div className="p-4 hover:bg-gray-50 dark:hover:bg-dark-gray-900">
        <NeurologicalRows
          heading="Cranial"
          onChange={onChangeValue}
          rowName="cranial"
          value={value}
          setChartOpen={setEditableCranialChartIsOpen}
          disabled={signed}
        />
      </div>
      <div className="p-4 hover:bg-gray-50 dark:hover:bg-dark-gray-900">
        <NeurologicalRows
          heading="Central"
          onChange={onChangeValue}
          rowName="central"
          value={value}
          setChartOpen={setEditableCentralChartIsOpen}
          disabled={signed}
        />
      </div>
      {!!close && (
        <div className="flex flex-row justify-end mt-4">
          <Button onClick={close} text="Close" color={ButtonColors.plainWithBorder} />
        </div>
      )}
      <EditableChart
        isOpen={editableCervicalChartIsOpen}
        close={() => setEditableCervicalChartIsOpen(false)}
        value={value?.cervical}
        colHeadings={NEUROLOGICAL_SPINAL_OPTIONS}
        rowHeadings={NEUROLOGICAL_CERVICAL_ROWS}
        tdChoice={NEUROLOGICAL_TD_OPTIONS}
        title="Spinal | Cervical Neurologic Test"
        abbreviations={NEUROLOGICAL_ABBREVIATIONS}
        onChange={(val: any) => {
          onChange({ ...value, cervical: val });
        }}
        wideCols={NEUROLOGICAL_WIDE_COLS}
        test="cervical"
        noDropdown={NEUROLOGICAL_NO_DROPDOWN}
        signed={signed}
      />
      <EditableChart
        isOpen={editableLumbarChartIsOpen}
        close={() => setEditableLumbarChartIsOpen(false)}
        value={value?.lumbar}
        colHeadings={NEUROLOGICAL_SPINAL_OPTIONS}
        rowHeadings={NEUROLOGICAL_LUMBAR_ROWS}
        tdChoice={NEUROLOGICAL_TD_OPTIONS}
        title="Spinal | Lumbar Neurologic Test"
        abbreviations={NEUROLOGICAL_ABBREVIATIONS}
        onChange={(val: any) => {
          onChange({ ...value, lumbar: val });
        }}
        wideCols={NEUROLOGICAL_WIDE_COLS}
        test="lumbar"
        noDropdown={NEUROLOGICAL_NO_DROPDOWN}
        signed={signed}
      />
      <EditableChart
        isOpen={editableUpperChartIsOpen}
        close={() => setEditableUpperChartIsOpen(false)}
        value={value?.upper || { result: 'normal', detail: detailInfo.upper }}
        colHeadings={NEUROLOGICAL_PERIPHERAL_OPTIONS}
        rowHeadings={NEUROLOGICAL_UPPER_ROWS}
        tdChoice={NEUROLOGICAL_PERIPHERAL_TD_OPTIONS}
        title="Peripheral | Upper Neurologic Test"
        abbreviations={NEUROLOGICAL_ABBREVIATIONS}
        onChange={(val: any) => {
          onChange({ ...value, upper: val });
        }}
        wideCols={NEUROLOGICAL_WIDE_COLS}
        test="upper"
        noDropdown={NEUROLOGICAL_NO_DROPDOWN}
        signed={signed}
      />
      <EditableChart
        isOpen={editableLowerChartIsOpen}
        close={() => setEditableLowerChartIsOpen(false)}
        value={value?.lower || { result: 'normal', detail: detailInfo.lower }}
        colHeadings={NEUROLOGICAL_PERIPHERAL_OPTIONS}
        rowHeadings={NEUROLOGICAL_LOWER_ROWS}
        tdChoice={NEUROLOGICAL_PERIPHERAL_TD_OPTIONS}
        title="Peripheral | Lower Neurologic Test"
        abbreviations={NEUROLOGICAL_ABBREVIATIONS}
        onChange={(val: any) => {
          onChange({ ...value, lower: val });
        }}
        wideCols={NEUROLOGICAL_WIDE_COLS}
        test="lower"
        noDropdown={NEUROLOGICAL_NO_DROPDOWN}
        signed={signed}
      />
      <CranialEditableChart
        isOpen={editableCranialChartIsOpen}
        close={() => setEditableCranialChartIsOpen(false)}
        value={value?.cranial}
        onChange={(val: any) => {
          onChange({ ...value, cranial: val });
        }}
        signed={signed}
      />
      <CentralEditableChart
        isOpen={editableCentralChartIsOpen}
        close={() => setEditableCentralChartIsOpen(false)}
        value={value?.central}
        onChange={(val: any) => {
          onChange({ ...value, central: val });
        }}
        signed={signed}
      />
    </div>
  );
};

export default UnifiedNeurological;
